/**
 * Credential templates for the Vault item form.
 *
 * Selecting a template decides which named fields the form shows. The field
 * VALUES are stored in the item's encrypted secret payload (arbitrary JSON) —
 * only `meta: true` fields (currently username) also go to a metadata column so
 * they can appear in list views. `itemType` maps the template to the underlying
 * DB enum; the precise template key is stored in the item's `template` column so
 * the form can re-render the right fields when editing.
 *
 * Every template also supports free-form custom fields on top of its presets.
 */
export interface TemplateField {
  key: string;
  label: string;
  secret?: boolean;    // masked + goes into the encrypted payload (default true)
  meta?: boolean;      // also stored as metadata (username) — shown in lists
  multiline?: boolean;
  generate?: boolean;  // show a "generate" button (passwords/secrets)
  placeholder?: string;
}

export interface CredentialTemplate {
  key: string;
  label: string;
  itemType: string;    // DB enum: website_login|database|api_key|ssh_key|secure_note|other
  icon: string;        // lucide icon name
  fields: TemplateField[];
}

export const CREDENTIAL_TEMPLATES: CredentialTemplate[] = [
  {
    key: 'website_login', label: 'Website login', itemType: 'website_login', icon: 'Globe',
    fields: [
      { key: 'username', label: 'Username', secret: false, meta: true, placeholder: 'user@example.com' },
      { key: 'password', label: 'Password', secret: true, generate: true },
      { key: 'url', label: 'URL', secret: false, placeholder: 'https://…' },
      { key: 'notes', label: 'Notes', secret: true, multiline: true },
    ],
  },
  {
    key: 'database', label: 'Database', itemType: 'database', icon: 'Database',
    fields: [
      { key: 'host', label: 'Host', secret: false, placeholder: 'db.internal' },
      { key: 'port', label: 'Port', secret: false, placeholder: '5432' },
      { key: 'database', label: 'Database name', secret: false },
      { key: 'username', label: 'Username', secret: false, meta: true },
      { key: 'password', label: 'Password', secret: true, generate: true },
      { key: 'notes', label: 'Notes', secret: true, multiline: true },
    ],
  },
  {
    key: 'api_key', label: 'API key', itemType: 'api_key', icon: 'KeyRound',
    fields: [
      { key: 'key', label: 'Key / client id', secret: true },
      { key: 'secret', label: 'Secret', secret: true, generate: true },
      { key: 'notes', label: 'Notes', secret: true, multiline: true },
    ],
  },
  {
    key: 's3', label: 'S3 bucket', itemType: 'other', icon: 'Cloud',
    fields: [
      { key: 'accessKeyId', label: 'Access key id', secret: true },
      { key: 'secretAccessKey', label: 'Secret access key', secret: true },
      { key: 'region', label: 'Region', secret: false, placeholder: 'ap-south-1' },
      { key: 'bucket', label: 'Bucket', secret: false },
      { key: 'endpoint', label: 'Endpoint', secret: false, placeholder: 'optional (S3-compatible)' },
      { key: 'notes', label: 'Notes', secret: true, multiline: true },
    ],
  },
  {
    key: 'smtp', label: 'SMTP / email', itemType: 'other', icon: 'Mail',
    fields: [
      { key: 'host', label: 'Host', secret: false, placeholder: 'email-smtp.ap-south-1.amazonaws.com' },
      { key: 'port', label: 'Port', secret: false, placeholder: '587' },
      { key: 'username', label: 'Username', secret: false, meta: true },
      { key: 'password', label: 'Password', secret: true },
      { key: 'fromAddress', label: 'From address', secret: false },
      { key: 'notes', label: 'Notes', secret: true, multiline: true },
    ],
  },
  {
    key: 'webhook', label: 'Webhook', itemType: 'other', icon: 'Webhook',
    fields: [
      { key: 'url', label: 'Webhook URL', secret: true },
      { key: 'signingSecret', label: 'Signing secret', secret: true, generate: true },
      { key: 'notes', label: 'Notes', secret: true, multiline: true },
    ],
  },
  {
    key: 'ssh_key', label: 'SSH key', itemType: 'ssh_key', icon: 'Terminal',
    fields: [
      { key: 'privateKey', label: 'Private key', secret: true, multiline: true },
      { key: 'passphrase', label: 'Passphrase', secret: true },
      { key: 'notes', label: 'Notes', secret: true, multiline: true },
    ],
  },
  {
    key: 'secure_note', label: 'Secure note', itemType: 'secure_note', icon: 'FileText',
    fields: [
      { key: 'note', label: 'Note', secret: true, multiline: true },
    ],
  },
  {
    key: 'custom', label: 'Custom', itemType: 'other', icon: 'SlidersHorizontal',
    fields: [],
  },
];

export const templateByKey = (key?: string | null): CredentialTemplate =>
  CREDENTIAL_TEMPLATES.find((t) => t.key === key) || CREDENTIAL_TEMPLATES[0];

/** Fallback when an item has no stored template (older rows): infer from itemType. */
export const templateForItemType = (itemType: string): CredentialTemplate =>
  CREDENTIAL_TEMPLATES.find((t) => t.itemType === itemType && t.key !== 'custom')
  || templateByKey('custom');
